import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowRight, Coins, Download, QrCode } from "lucide-react";
import type { Paginated, StockDto, UserDto } from "@gatiq/shared";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Modal,
  Page,
  PageBody,
  PageHeader,
  Skeleton,
  StatCard,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { useCan } from "@/auth/access";
import { useOrgRoles } from "@/auth/useOrgRoles";
import { QrPosterPanel } from "@/components/QrPosterPanel";

interface MeCounter extends UserDto {
  registrationUrl: string | null;
  counts: { total: number; pending: number; approved: number; thisMonth: number };
}

export function CounterDashboard() {
  const toast = useToast();
  const navigate = useNavigate();
  const [me, setMe] = useState<MeCounter | null>(null);
  const [pending, setPending] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [qrOpen, setQrOpen] = useState(false);
  const [designPosterOnOpen, setDesignPosterOnOpen] = useState(false);
  // The waiting list is karigar data; a counter whose role lost it just doesn't get the card's rows.
  const seeKarigars = Boolean(useCan()("karigars"));
  // D-20: with dispatch on, the counter sees what it holds.
  const seeStock = Boolean(useOrgRoles()?.features.dispatch) && Boolean(useCan()("dispatch"));
  const [stock, setStock] = useState<{ inStock: number; sold: number } | null>(null);
  const [stockFailed, setStockFailed] = useState(false);

  useEffect(() => {
    if (!seeStock) return;
    api
      .get<StockDto>("/org/stock")
      .then((res) => {
        const mine = res.rows.find((r) => r.holder.type === "counter");
        setStock({ inStock: mine?.inStock ?? 0, sold: mine?.sold ?? 0 });
      })
      .catch(() => setStockFailed(true));
  }, [seeStock]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      /*
       * `/org/me/karigars` and not `/org/approvals`. Approving is the brand's
       * decision now, so the approvals queue is closed to a counter and asking
       * for it returned 403 on every dashboard load. What a counter is owed
       * here is the state of the people they signed up, which is their own
       * data and a different question.
       */
      const [counter, waiting] = await Promise.all([
        api.get<MeCounter>("/org/me/counter"),
        seeKarigars
          ? api.get<Paginated<UserDto>>("/org/me/karigars?kycStatus=pending&page=1&pageSize=3")
          : Promise.resolve({ items: [] as UserDto[] }),
      ]);
      setMe(counter);
      setPending(waiting.items);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load your dashboard.");
    } finally {
      setLoading(false);
    }
  }, [seeKarigars, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Page>
      <PageHeader title={me?.name ?? "Dashboard"} />

      <PageBody className="flex flex-col gap-5 pb-2 [&>*]:shrink-0">
        {/* The counter's two jobs — signing karigars up and paying them out —
            lead the screen instead of sitting in a card below the numbers. */}
        <section className="relative overflow-hidden rounded-lg bg-panel px-6 py-6 text-panel-text shadow-card sm:px-8">
          <div
            aria-hidden="true"
            className="gq-modules pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-[0.08] [mask-image:linear-gradient(to_left,black,transparent)]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 right-24 size-72 rounded-full bg-brand/30 blur-[80px]"
          />
          <div className="relative flex flex-wrap items-end justify-between gap-5">
            <div className="min-w-0">
              <p className="text-sm text-panel-muted">Your counter</p>
              <h2 className="mt-1 truncate font-title text-2xl leading-tight font-semibold tracking-[-0.02em] sm:text-[1.75rem]">
                {me?.name ?? <span className="inline-block h-7 w-48 animate-pulse rounded-md bg-panel-text/15 align-middle" />}
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button icon={<QrCode aria-hidden="true" className="size-4" />} onClick={() => { setDesignPosterOnOpen(false); setQrOpen(true); }}>
                Show QR
              </Button>
              <Button
                icon={<Coins aria-hidden="true" className="size-4" />}
                onClick={() => navigate("redemptions")}
                variant="inverse"
              >
                Pay a karigar
              </Button>
              <Button
                icon={<Download aria-hidden="true" className="size-4" />}
                onClick={() => { setDesignPosterOnOpen(true); setQrOpen(true); }}
                variant="inverse"
              >
                Design poster
              </Button>
            </div>
          </div>
        </section>

        <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${seeStock ? "lg:grid-cols-4" : ""}`}>
          <StatCard label="My karigars" value={me ? me.counts.total : <Skeleton className="h-8 w-12" />} />
          <StatCard
            hint={me && me.counts.pending > 0 ? "with the brand for approval" : undefined}
            label="Pending"
            tone={me && me.counts.pending > 0 ? "attention" : "neutral"}
            value={me ? me.counts.pending : <Skeleton className="h-8 w-12" />}
          />
          <StatCard
            hint="new karigars"
            label="This month"
            value={me ? `+${me.counts.thisMonth}` : <Skeleton className="h-8 w-12" />}
          />
          {seeStock && !stockFailed ? (
            <StatCard
              hint={stock ? `${stock.sold.toLocaleString("en-IN")} sold` : undefined}
              label="My stock"
              value={stock ? stock.inStock.toLocaleString("en-IN") : <Skeleton className="h-8 w-12" />}
            />
          ) : null}
        </div>

        <Card>
          <CardHeader
            action={
              <Link
                className="inline-flex items-center gap-1 text-sm font-medium text-brand-ink hover:underline"
                to="karigars"
              >
                View all <ArrowRight aria-hidden="true" className="size-3.5" />
              </Link>
            }
            title={`Waiting for approval (${me?.counts.pending ?? 0})`}
          />
          {loading ? (
            <CardBody>
              <Skeleton className="h-10 w-full" />
            </CardBody>
          ) : pending.length === 0 ? (
            <EmptyState
              message="Everyone you have signed up has been approved."
              title="Nobody is waiting"
            />
          ) : (
            <ul className="divide-y divide-rule/70">
              {pending.map((k) => (
                <li className="flex items-center gap-3 px-5 py-3" key={k.id}>
                  <Avatar name={k.name} src={k.photoUrl} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{k.name}</p>
                    <p className="truncate text-[13px] text-muted">{k.phone}</p>
                  </div>
                  {/* No button: the counter is being told, not asked. */}
                  <Badge tone="warning">Pending</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </PageBody>

      <Modal onClose={() => { setQrOpen(false); setDesignPosterOnOpen(false); }} open={qrOpen} title={designPosterOnOpen ? "Design registration poster" : "Registration QR"}>
        {me?.registrationUrl ? (
          <QrPosterPanel
            counterName={me.name}
            counterPhone={me.phone}
            openDesignOnMount={designPosterOnOpen}
            qrPngUrl="/api/org/me/counter/qr.png"
            registrationUrl={me.registrationUrl}
          />
        ) : null}
      </Modal>
    </Page>
  );
}
